import { Dayjs } from 'dayjs';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import type {
  TDateInput,
  TRangeValue,
  TViewMode,
} from '~/interfaces/components/date-picker';

const toDay = (v: TDateInput, fallback?: Dayjs): Dayjs | null => {
  const { $dayjs } = useNuxtApp();

  if (v === null || v === undefined) return fallback ?? null;
  const d = $dayjs.isDayjs(v)
    ? v
    : typeof v === 'string'
      ? $dayjs(v)
      : $dayjs(v as Date | number);
  return d.isValid() ? d : (fallback ?? null);
};
const clampToBounds = (d: Dayjs, min: Dayjs | null, max: Dayjs | null) => {
  if (min && d.isBefore(min, 'day')) return min;
  if (max && d.isAfter(max, 'day')) return max;
  return d;
};
const isOutOfBounds = (d: Dayjs, min: Dayjs | null, max: Dayjs | null) => {
  if (!d) return true;
  if (min && d.isBefore(min, 'day')) return true;
  return !!(max && d.isAfter(max, 'day'));
};
const normalizeStart = (d: Dayjs | null) => (d ? d.startOf('day') : null);
const normalizeEnd = (d: Dayjs | null) => (d ? d.endOf('day') : null);

const useDatePicker = ({
  minDate,
  maxDate,
  mode,
  modelValue,
  format,
  placeholder,
  startDatePlaceholder,
  endDatePlaceholder,
  closeOnSelect,
}: {
  minDate?: TDateInput;
  maxDate?: TDateInput;
  mode: 'single' | 'range';
  modelValue: Ref<TDateInput | TRangeValue | null>;
  format: string;
  placeholder: string;
  startDatePlaceholder: string;
  endDatePlaceholder: string;
  closeOnSelect?: boolean;
}) => {
  const { $t, $dayjs } = useNuxtApp();

  const today = $dayjs();
  const open = ref(false);
  const viewMode = ref<TViewMode>('days');
  const isRange = computed(() => mode === 'range');
  const min = computed(() => toDay(minDate));
  const max = computed(() => toDay(maxDate));

  const modelSingle = computed<Dayjs | null>({
    get: () =>
      isRange.value
        ? null
        : normalizeStart(toDay(modelValue.value as TDateInput)),
    set: (v) => (modelValue.value = v ? v.startOf('day') : v),
  });

  const modelRange = computed<{
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }>({
    get: () => {
      if (!isRange.value) return { startDate: null, endDate: null };
      const val = (modelValue.value as TRangeValue) || null;
      return {
        startDate: normalizeStart(toDay(val?.startDate ?? null)),
        endDate: normalizeEnd(toDay(val?.endDate ?? null)),
      };
    },
    set: (v) =>
      (modelValue.value =
        v.startDate || v.endDate
          ? { startDate: v.startDate, endDate: v.endDate }
          : null),
  });

  const initialViewDate = computed(() => {
    if (isRange.value) {
      return (
        modelRange.value.startDate ||
        modelRange.value.endDate ||
        clampToBounds(today, min.value, max.value)
      );
    }
    return modelSingle.value || clampToBounds(today, min.value, max.value);
  });

  const viewYear = ref<number>(initialViewDate.value.year());
  const viewMonth = ref<number>(initialViewDate.value.month());

  const dayNames = computed(() => [
    $t('mo'),
    $t('tu'),
    $t('we'),
    $t('th'),
    $t('fr'),
    $t('sa'),
    $t('su'),
  ]);

  const monthNames = computed(() => [
    $t('january'),
    $t('february'),
    $t('march'),
    $t('april'),
    $t('may'),
    $t('june'),
    $t('july'),
    $t('august'),
    $t('september'),
    $t('october'),
    $t('november'),
    $t('december'),
  ]);

  const monthOptions = computed(() =>
    monthNames.value.map((label, value) => {
      const date = $dayjs().year(viewYear.value).month(value).date(1);
      const disabled =
        isOutOfBounds(date, min.value, max.value) &&
        isOutOfBounds(date.endOf('month'), min.value, max.value);
      return { label, value, disabled };
    }),
  );

  const yearOptions = computed(() => {
    const minYear = min.value ? min.value.year() : viewYear.value - 100;
    const maxYear = max.value ? max.value.year() : viewYear.value + 100;
    const list: { label: string; value: number; disabled: boolean }[] = [];
    for (let y = minYear; y <= maxYear; y++) {
      const first = $dayjs().year(y).month(0).date(1);
      const last = $dayjs().year(y).month(11).date(31);
      const blocked =
        isOutOfBounds(last, min.value, max.value) &&
        isOutOfBounds(first, min.value, max.value);
      list.push({ label: String(y), value: y, disabled: blocked });
    }
    return list;
  });

  const displaySingle = computed(() =>
    modelSingle.value ? modelSingle.value.format(format) : $t(placeholder),
  );

  const displayRangeStart = computed(() =>
    modelRange.value.startDate
      ? modelRange.value.startDate.format(format)
      : $t(startDatePlaceholder),
  );

  const displayRangeEnd = computed(() =>
    modelRange.value.endDate
      ? modelRange.value.endDate.format(format)
      : $t(endDatePlaceholder),
  );

  const showReset = computed(() => {
    if (isRange.value)
      return !!modelRange.value?.startDate || !!modelRange.value?.endDate;
    return !!modelSingle.value;
  });

  const buildCalendar = (year: number, month0: number) => {
    const firstOfMonth = $dayjs().year(year).month(month0).date(1);
    const daysInMonth = firstOfMonth.daysInMonth();
    const startIdx = (firstOfMonth.day() + 6) % 7;

    const grid: {
      date: Dayjs;
      inCurrentMonth: boolean;
      disabled: boolean;
      isToday: boolean;
    }[] = [];

    for (let i = startIdx - 1; i >= 0; i--) {
      const date = $dayjs(firstOfMonth).subtract(i + 1, 'day');
      grid.push({
        date,
        inCurrentMonth: false,
        disabled: isOutOfBounds(date, min.value, max.value),
        isToday: date.isSame(today, 'day'),
      });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = $dayjs(firstOfMonth).date(d);
      grid.push({
        date,
        inCurrentMonth: true,
        disabled: isOutOfBounds(date, min.value, max.value),
        isToday: date.isSame(today, 'day'),
      });
    }

    const tail = (7 - (grid.length % 7)) % 7;
    for (let i = 1; i <= tail; i++) {
      const date = $dayjs(firstOfMonth).add(daysInMonth + i - 1, 'day');
      grid.push({
        date,
        inCurrentMonth: false,
        disabled: isOutOfBounds(date, min.value, max.value),
        isToday: date.isSame(today, 'day'),
      });
    }

    return grid;
  };

  const grid = computed(() => buildCalendar(viewYear.value, viewMonth.value));

  const canGoPrevMonth = computed(() => {
    const prev = $dayjs()
      .year(viewYear.value)
      .month(viewMonth.value)
      .date(min.value?.date() ?? 1)
      .subtract(1, 'month');
    return !isOutOfBounds(prev, min.value, max.value);
  });

  const canGoNextMonth = computed(() => {
    const next = $dayjs()
      .year(viewYear.value)
      .month(viewMonth.value)
      .date(max.value?.date() ?? 1)
      .add(1, 'month');
    return !isOutOfBounds(next, min.value, max.value);
  });

  const canGoPrevYear = computed(() => {
    if (!min.value) return true;
    const endOfPrevYear = $dayjs()
      .year(viewYear.value - 1)
      .month(11)
      .endOf('month');
    return !isOutOfBounds(endOfPrevYear, min.value, max.value);
  });

  const canGoNextYear = computed(() => {
    if (!max.value) return true;
    const startOfNextYear = $dayjs()
      .year(viewYear.value + 1)
      .month(0)
      .startOf('month');
    return !isOutOfBounds(startOfNextYear, min.value, max.value);
  });

  const goPrevMonth = () => {
    if (!canGoPrevMonth.value) return;
    const prev = $dayjs()
      .year(viewYear.value)
      .month(viewMonth.value)
      .date(1)
      .subtract(1, 'month');
    viewYear.value = prev.year();
    viewMonth.value = prev.month();
  };

  const goNextMonth = () => {
    if (!canGoNextMonth.value) return;
    const next = $dayjs()
      .year(viewYear.value)
      .month(viewMonth.value)
      .date(1)
      .add(1, 'month');
    viewYear.value = next.year();
    viewMonth.value = next.month();
  };

  const goPrevYear = () => {
    if (!canGoPrevYear.value) return;
    const targetYear = viewYear.value - 1;
    const targetDate = $dayjs().year(targetYear).month(viewMonth.value).date(1);
    viewYear.value = targetYear;
    if (min.value && targetDate.isBefore(min.value, 'month')) {
      viewMonth.value = min.value.month();
    }
  };

  const goNextYear = () => {
    if (!canGoNextYear.value) return;
    const targetYear = viewYear.value + 1;
    const targetDate = $dayjs().year(targetYear).month(viewMonth.value).date(1);
    viewYear.value = targetYear;
    if (max.value && targetDate.isAfter(max.value, 'month')) {
      viewMonth.value = max.value.month();
    }
  };

  const chooseMonth = (m: number) => {
    viewMonth.value = m;
    viewMode.value = 'days';
  };

  const chooseYear = (y: number) => {
    viewYear.value = y;
    viewMode.value = 'days';
  };

  const isSelectedStart = (d: Dayjs) =>
    !!modelRange.value.startDate && d.isSame(modelRange.value.startDate, 'day');

  const isSelectedEnd = (d: Dayjs) =>
    !!modelRange.value.endDate && d.isSame(modelRange.value.endDate, 'day');

  const isWithinSelected = (d: Dayjs) =>
    !!modelRange.value.startDate &&
    !!modelRange.value.endDate &&
    d.isAfter(modelRange.value.startDate, 'day') &&
    d.isBefore(modelRange.value.endDate, 'day');

  const onDayClick = (
    d: Dayjs,
    opts?: {
      emitConfirm?: (val: Dayjs | { startDate: Dayjs; endDate: Dayjs }) => void;
    },
  ) => {
    if (isOutOfBounds(d, min.value, max.value)) return;

    if (!isRange.value) {
      modelSingle.value = d;
      if (closeOnSelect) {
        open.value = false;
        if (opts?.emitConfirm) opts.emitConfirm(d);
      }
      return;
    }

    const { startDate, endDate } = modelRange.value;

    if (!startDate && !endDate) {
      modelRange.value = { startDate: d, endDate: null };
      return;
    }

    if (startDate && !endDate) {
      if (d.isBefore(startDate, 'day')) {
        modelRange.value = { startDate: d, endDate: startDate };
        handleCloseOnSelect({ emitConfirm: opts?.emitConfirm });
      } else if (d.isSame(startDate, 'day')) {
        modelRange.value = { startDate: d, endDate: d };
      } else {
        modelRange.value = { startDate, endDate: d };
        handleCloseOnSelect({ emitConfirm: opts?.emitConfirm });
      }
      return;
    }

    modelRange.value = { startDate: d, endDate: null };
  };

  const handleCloseOnSelect = (opts: {
    emitConfirm?: (val: Dayjs | { startDate: Dayjs; endDate: Dayjs }) => void;
  }) => {
    if (closeOnSelect) {
      open.value = false;
      if (opts?.emitConfirm) opts.emitConfirm(modelRange.value);
    }
  };

  const resetCalendar = () => {
    viewYear.value = initialViewDate.value.year();
    viewMonth.value = initialViewDate.value.month();
  };

  const resetSelection = (emitReset?: () => void) => {
    if (isRange.value) modelRange.value = { startDate: null, endDate: null };
    else modelSingle.value = null;
    emitReset?.();
    resetCalendar();
  };

  const confirmRange = (
    emitConfirm?: (val: Dayjs | { startDate: Dayjs; endDate: Dayjs }) => void,
  ) => {
    if (!isRange.value) {
      if (modelSingle.value) emitConfirm?.(modelSingle.value);
      return;
    }
    if (modelRange.value.startDate && modelRange.value.endDate) {
      emitConfirm?.({
        startDate: modelRange.value.startDate,
        endDate: modelRange.value.endDate,
      });
    }
  };

  const togglePopup = (v?: boolean) => {
    open.value = typeof v === 'boolean' ? v : !open.value;
  };

  const closePopup = () => {
    open.value = false;
  };

  return {
    canGoNextMonth,
    canGoPrevMonth,
    canGoNextYear,
    canGoPrevYear,
    dayNames,
    displayRangeEnd,
    displayRangeStart,
    displaySingle,
    grid,
    isRange,
    max,
    min,
    modelRange,
    modelSingle,
    monthNames,
    monthOptions,
    open,
    showReset,
    today,
    viewMode,
    viewMonth,
    viewYear,
    yearOptions,
    chooseMonth,
    chooseYear,
    clampToBounds,
    closePopup,
    confirmRange,
    goNextMonth,
    goPrevMonth,
    goNextYear,
    goPrevYear,
    isOutOfBounds,
    isSelectedEnd,
    isSelectedStart,
    isWithinSelected,
    onDayClick,
    resetCalendar,
    resetSelection,
    togglePopup,
  };
};

export default useDatePicker;
